// src/components/TodayEntryModal.tsx
import React, { useState } from 'react';
import axios from 'axios';
import '../styles/Dashboard.css'; // Using the same CSS

interface TodayEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const API_BASE_URL = window.location.hostname === 'flabs2fabs.omchat.ovh' 
  ? 'https://flabs2fabs.omchat.ovh'
  : 'http://localhost:8008';

const TodayEntryModal: React.FC<TodayEntryModalProps> = ({ isOpen, onClose, onSave }) => {
  const [exercise, setExercise] = useState('');
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('flab2fabs_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Create workout
      const response = await axios.post(`${API_BASE_URL}/api/workouts/`, {
        name: `${exercise} Workout`,
        notes: notes || `Weight: ${weight}kg, Reps: ${reps}`,
        exercises: [{
          exercise_id: 1, // You'll need to map this properly
          sets: 3,
          reps: parseInt(reps) || 8,
          weight_kg: parseFloat(weight) || 60
        }]
      }, { headers });

      if (response.data?.id) {
        // Complete the workout
        await axios.post(
          `${API_BASE_URL}/api/workouts/${response.data.id}/complete`,
          {},
          { headers }
        );
        
        onSave();
        resetForm();
      }
    } catch (err: any) {
      console.error('Error saving workout:', err);
      setError(err.response?.data?.detail || 'Failed to save workout');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setExercise('');
    setWeight('');
    setReps('');
    setNotes('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">💪 Log Today's Workout</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Exercise</label>
            <input
              type="text"
              value={exercise}
              onChange={(e) => setExercise(e.target.value)}
              placeholder="e.g., Barbell Bench Press"
              required
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Weight (kg)</label>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="60"
                required
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>Reps</label>
              <input
                type="number"
                value={reps}
                onChange={(e) => setReps(e.target.value)}
                placeholder="8"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How did it feel? Any adjustments?"
              rows={3}
              disabled={loading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button 
              type="button" 
              className="secondary-btn" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="primary-btn"
              disabled={loading}
            >
              {loading ? 'Saving...' : '💾 Save Workout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TodayEntryModal;
